/**
 * CartPage — Full shopping cart matching live MRT app layout
 *
 * Layout:
 *   Left (main): Cart items table with product details, editable qty,
 *                 bundle warnings, remove item, clear cart
 *   Right (sidebar): QuickAdd (Quick add / Import tabs),
 *                     Cart total, Request a quote / Submit an order
 *
 * Bundle qty warnings: red text when qty is not a multiple of bundledQty
 * Estimated additional cost: shown per line when bundle violation
 *
 * Contact triggers:
 *   - "Request a quote" → navigates to /contact?topic=request-order
 *   - "Contact us" links → request-availability topic
 *
 * @module pages/CartPage
 * @since 2026-05-25 (Group G — commerce flow)
 */
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Breadcrumbs from '@components/layout/Breadcrumbs';
import ContactHelpBanner from '@components/contact/ContactHelpBanner';
import QuickAdd from '@components/commerce/QuickAdd';
import { useCart } from '@contexts/CartContext';
import { useContactModal } from '@contexts/ContactModalContext';
import useAuth from '@hooks/useAuth';

// ── Helpers ───────────────────────────────────────────────────────

function formatCurrency(amount, currency = 'CAD') {
  if (amount == null || isNaN(amount)) return '—';
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(amount);
}

function formatWeight(kg, lb) {
  if (!lb && !kg) return '—';
  if (lb) return `${Number(lb).toFixed(1)} lb`;
  return `${Number(kg).toFixed(1)} kg`;
}

/** Check if qty violates bundle size; returns { isBundleViolation, additionalCost } */
function checkBundle(qty, bundledQty, shippingCost) {
  if (!bundledQty || bundledQty <= 1) return { isBundleViolation: false, additionalCost: 0 };
  const isBundleViolation = qty % bundledQty !== 0;
  const additionalCost = isBundleViolation ? (shippingCost || 0) : 0;
  return { isBundleViolation, additionalCost };
}

// ── CartItemRow ───────────────────────────────────────────────────

function CartItemRow({ item, currency, onUpdateQty, onRemove, onContactAvailability }) {
  const { isBundleViolation, additionalCost } = checkBundle(
    item.quantity, item.bundledQty, item.shippingCost
  );
  const lineWeight = (item.weightLb || 0) * item.quantity;

  return (
    <tr className="border-b border-n1 hover:bg-n1/30">
      {/* Image */}
      <td className="px-4 py-4 align-top">
        {item.thumbnailUrl || item.imageUrl ? (
          <img
            src={item.thumbnailUrl || item.imageUrl}
            alt={item.description || item.itemCode}
            className="w-14 h-14 object-contain bg-n1 border border-n2"
            loading="lazy"
          />
        ) : (
          <div className="w-14 h-14 bg-n1 border border-n2 flex items-center justify-center">
            <span className="material-symbols-outlined text-n3 text-xl">
              {item.productLine === 'dth' ? 'hardware' : 'construction'}
            </span>
          </div>
        )}
      </td>

      {/* Product details */}
      <td className="px-4 py-4 align-top">
        <p className="text-carbon-black font-normal text-sm mb-1">{item.description}</p>
        <p className="text-xs text-n5 mb-2">
          <Link to={`/product/${item.itemCode}`} className="text-electric-blue hover:underline">
            {item.itemCode}
          </Link>
        </p>

        <div className="space-y-1 text-xs text-n5">
          {/* Availability */}
          <div className="flex items-center gap-1">
            <span className="material-symbols-outlined text-xs">info</span>
            <span>Availability: </span>
            {item.availability === 'Contact us' ? (
              <button
                onClick={() => onContactAvailability(item.itemCode)}
                className="text-electric-blue hover:underline"
              >
                Contact us &gt;
              </button>
            ) : (
              <span className="text-success">{item.availability || 'In stock'}</span>
            )}
          </div>

          {/* Estimated delivery */}
          {item.estimatedDeliveryDays && (
            <div className="flex items-center gap-1">
              <span className="material-symbols-outlined text-xs">info</span>
              <span>Est. delivery: {item.estimatedDeliveryDays} business days</span>
            </div>
          )}

          {/* Price */}
          <div className="flex items-center gap-1">
            <span className="material-symbols-outlined text-xs">info</span>
            <span>Price: {formatCurrency(item.unitPrice)} / unit</span>
          </div>

          {/* Weight */}
          <div className="flex items-center gap-1">
            <span className="material-symbols-outlined text-xs">info</span>
            <span>Weight: {formatWeight(item.weightKg, item.weightLb)}</span>
            {lineWeight > 0 && <span className="text-n4">({lineWeight.toFixed(1)} lb total)</span>}
          </div>

          {/* Estimated additional cost */}
          <div className="flex items-center gap-1">
            <span className="material-symbols-outlined text-xs">info</span>
            <span>Est. additional cost: {formatCurrency(additionalCost)}</span>
          </div>
        </div>

        {/* Bundle warning */}
        {isBundleViolation && (
          <div className="mt-2 text-xs text-red-600 font-normal flex items-start gap-1">
            <span className="material-symbols-outlined text-sm mt-0.5">warning</span>
            <span>
              This item is shipped in packages of {item.bundledQty}, if you buy less quantity,
              additional costs may apply
            </span>
          </div>
        )}

        {/* Remove item */}
        <button
          onClick={() => onRemove(item.productId)}
          className="mt-2 text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
        >
          <span className="material-symbols-outlined text-sm">delete</span>
          Remove item
        </button>
      </td>

      {/* Quantity */}
      <td className="px-4 py-4 align-top">
        <input
          type="number"
          min="1"
          value={item.quantity}
          onChange={e => onUpdateQty(item.productId, Math.max(1, parseInt(e.target.value) || 1))}
          className="w-20 border border-n3 px-2 py-1.5 text-sm text-center focus:ring-1 focus:ring-electric-blue focus:border-electric-blue"
        />
      </td>

      {/* Total */}
      <td className="px-4 py-4 align-top text-right">
        <span className="text-sm font-normal text-carbon-black">
          {formatCurrency(item.unitPrice * item.quantity)}
        </span>
      </td>
    </tr>
  );
}

// ── CartPage (main) ───────────────────────────────────────────────

export default function CartPage() {
  const { state, dispatch, itemCount } = useCart();
  const { openContact } = useContactModal();
  const { isGuest, hasPermission } = useAuth();
  const canExportCSV = hasPermission('export_cart');
  const navigate = useNavigate();

  const cartTotal = useMemo(
    () => state.items.reduce((sum, item) => sum + (item.unitPrice || 0) * item.quantity, 0),
    [state.items]
  );

  const totalWeight = useMemo(
    () => state.items.reduce((sum, item) => sum + (item.weightLb || 0) * item.quantity, 0),
    [state.items]
  );

  const totalAdditionalCost = useMemo(
    () => state.items.reduce((sum, item) => {
      const { additionalCost } = checkBundle(item.quantity, item.bundledQty, item.shippingCost);
      return sum + additionalCost;
    }, 0),
    [state.items]
  );

  const handleUpdateQty = (productId, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { productId, quantity } });
  };

  const handleRemove = (productId) => {
    dispatch({ type: 'REMOVE_ITEM', payload: productId });
  };

  const handleClearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const handleContactAvailability = (itemCode) => {
    openContact('request-availability', { itemCode });
  };

  // G10: Export cart as CSV download
  const handleExportCSV = () => {
    const header = ['Item Code', 'Description', 'Quantity', 'Unit Price', 'Line Total', 'Availability'];
    const rows = state.items.map(item => [
      item.itemCode || item.productId || '',
      `"${(item.description || item.name || '').replace(/"/g, '""')}"`,
      item.quantity,
      item.unitPrice != null && !isNaN(item.unitPrice) ? item.unitPrice.toFixed(2) : '',
      item.unitPrice != null && !isNaN(item.unitPrice) ? (item.unitPrice * item.quantity).toFixed(2) : '',
      item.availability || '',
    ]);
    const csv = [header.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `mrt-cart-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // G9: Guest gate — guests cannot access the cart
  if (isGuest) {
    return (
      <div className="sandvik-container py-6">
        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Cart' }]} />
        <div className="border border-n2 p-10 text-center">
          <span className="material-symbols-outlined text-5xl text-n3 mb-3 block">lock</span>
          <h2 className="font-sandvik-headline font-normal text-xl text-carbon-black mb-2">
            Sign in to view your cart
          </h2>
          <p className="text-n5 font-sandvik-text mb-4 text-sm">
            You need to be signed in to add items to your cart and place orders.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-electric-blue text-white text-sm hover:bg-eb-3 transition-colors"
          >
            <span className="material-symbols-outlined text-base">login</span>
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="sandvik-container py-6">
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Cart' }]} />

      <div className="flex items-center justify-between mb-4">
        <h1 className="font-sandvik-headline font-normal text-2xl text-carbon-black">Cart</h1>
        {state.items.length > 0 && (
          <div className="flex items-center gap-4">
            {canExportCSV && (
              <button
                onClick={handleExportCSV}
                className="text-sm text-electric-blue hover:text-eb-3 flex items-center gap-1 transition-colors"
              >
                <span className="material-symbols-outlined text-base">download</span>
                Export CSV
              </button>
            )}
            <button
              onClick={handleClearCart}
              className="text-sm text-n5 hover:text-red-600 flex items-center gap-1 transition-colors"
            >
              <span className="material-symbols-outlined text-base">delete_sweep</span>
              Clear Cart
            </button>
          </div>
        )}
      </div>

      {state.items.length === 0 ? (
        /* ── Empty cart ──────────────────────────────────── */
        <div className="border border-n2 p-10 text-center mb-6">
          <span className="material-symbols-outlined text-5xl text-n3 mb-3 block">shopping_cart</span>
          <p className="text-n5 font-sandvik-text mb-4">Your cart is empty</p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-electric-blue text-white text-sm hover:bg-eb-3 transition-colors"
          >
            <span className="material-symbols-outlined text-base">storefront</span>
            Browse catalog
          </Link>
        </div>
      ) : (
        /* ── Cart with items ─────────────────────────────── */
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left: Cart items table */}
          <div className="flex-1 min-w-0">
            <div className="border border-n2">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-n2 bg-n1 text-xs text-n5">
                      <th className="text-left px-4 py-3 font-normal w-16">Image</th>
                      <th className="text-left px-4 py-3 font-normal">Product</th>
                      <th className="text-center px-4 py-3 font-normal w-24">Quantity</th>
                      <th className="text-right px-4 py-3 font-normal w-24">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.items.map(item => (
                      <CartItemRow
                        key={item.productId}
                        item={item}
                        currency="CAD"
                        onUpdateQty={handleUpdateQty}
                        onRemove={handleRemove}
                        onContactAvailability={handleContactAvailability}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Weight summary */}
            {totalWeight > 0 && (
              <p className="text-xs text-n5 mt-2 px-1">
                Approximate total weight: {totalWeight.toFixed(1)} lb
              </p>
            )}
          </div>

          {/* Right: Sidebar */}
          <div className="w-full lg:w-80 flex-shrink-0 space-y-4">
            {/* Quick Add / Import */}
            <div className="border border-n2 p-4">
              <QuickAdd />
            </div>

            {/* Cart total + action buttons */}
            <div className="border border-n2 p-4">
              <h3 className="text-sm text-n5 mb-2">Cart total</h3>
              <p className="text-2xl font-sandvik-headline text-carbon-black mb-1">
                {formatCurrency(cartTotal)}
              </p>
              <p className="text-xs text-n5 mb-1">
                {itemCount} item{itemCount !== 1 ? 's' : ''}
              </p>
              {totalAdditionalCost > 0 && (
                <p className="text-xs text-warning mb-1">
                  + {formatCurrency(totalAdditionalCost)} est. additional packaging
                </p>
              )}
              <p className="text-xs text-n4 mb-4">
                Taxes and shipping calculated at checkout
              </p>

              <div className="space-y-2">
                <button
                  onClick={() => openContact('request-order', { itemCount, cartTotal })}
                  className="w-full px-4 py-2.5 border border-n3 text-sm text-carbon-black hover:border-electric-blue hover:text-electric-blue transition-colors flex items-center justify-center gap-2"
                >
                  <span className="material-symbols-outlined text-base">request_quote</span>
                  Request a quote
                </button>
                <button
                  onClick={() => navigate('/checkout')}
                  className="w-full px-4 py-2.5 bg-electric-blue text-white text-sm hover:bg-eb-3 transition-colors flex items-center justify-center gap-2"
                >
                  <span className="material-symbols-outlined text-base">shopping_cart_checkout</span>
                  Submit an order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6">
        <ContactHelpBanner
          title="Need assistance with your order?"
          topics={[
            { topicId: 'request-availability', label: 'Request availability', icon: 'inventory' },
            { topicId: 'request-order', label: 'Request a quote', icon: 'request_quote' },
            { topicId: 'others', label: 'General enquiry', icon: 'chat' },
          ]}
        />
      </div>
    </div>
  );
}
